// ============================================
// LEAD ALERT OVERLAY — F22A HOTFIX
// ============================================
// Alerta prominente GLOBAL para leads calificados HOT/WARM
// - Se muestra sobre TODO BiLeads (cualquier página)
// - Animación pulsante imposible de ignorar
// - Sonido de alerta
// - Botón "IR AL CHAT" que navega directo
// - Auto-dismiss después de 45 segundos
// - Stack de múltiples alertas
//
// USO: Poner en App.jsx DENTRO de SocketProvider + AuthProvider:
//   <LeadAlertOverlay />
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import notificationService from '../services/notificationServiceFE';  // ✅ HOTFIX3

// ============================================
// SONIDO DE ALERTA (generado inline, sin archivo externo)
// ============================================
const playAlertSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    
    // Triple beep urgente
    [0, 0.2, 0.4].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = 880; // A5
      gain.gain.value = 0.3;
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.12);
    });
    
    // Cerrar después
    setTimeout(() => ctx.close(), 1500);
  } catch (e) {
    // Silencioso si no puede reproducir
  }
};

// ============================================
// ESTILOS
// ============================================
const keyframes = `
@keyframes leadAlertSlideIn {
  from { transform: translateY(-100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@keyframes leadAlertPulse {
  0%, 100% { box-shadow: 0 4px 30px rgba(239, 68, 68, 0.3); }
  50% { box-shadow: 0 4px 50px rgba(239, 68, 68, 0.6), 0 0 80px rgba(239, 68, 68, 0.2); }
}
@keyframes leadAlertPulseWarm {
  0%, 100% { box-shadow: 0 4px 30px rgba(245, 158, 11, 0.3); }
  50% { box-shadow: 0 4px 50px rgba(245, 158, 11, 0.6), 0 0 80px rgba(245, 158, 11, 0.2); }
}
@keyframes urgentBlink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
@keyframes progressShrink {
  from { width: 100%; }
  to { width: 0%; }
}
`;

const LeadAlertOverlay = () => {
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();
  const alertIdRef = useRef(0);

  // Socket
  const socketContext = useSocket();
  const socket = socketContext?.socket || socketContext;

  // Inyectar keyframes una vez
  useEffect(() => {
    if (document.getElementById('lead-alert-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'lead-alert-keyframes';
    style.textContent = keyframes;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('lead-alert-keyframes');
      if (el) el.remove();
    };
  }, []);

  // ✅ HOTFIX3: Ref para tracking notificaciones ya alertadas
  const alertedIdsRef = useRef(new Set());

  // ============================================
  // SOCKET: Escuchar nueva_notificacion
  // ============================================
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return;

    const handleNuevaNotificacion = (data) => {
      const { notificacion } = data;
      if (!notificacion) return;

      // Solo alertar para LEAD_CALIFICADO con temperatura CALIENTE o TIBIO
      const temp = notificacion.metadata?.temperatura;
      if (
        notificacion.tipo === 'LEAD_CALIFICADO' &&
        (temp === 'CALIENTE' || temp === 'TIBIO')
      ) {
        showAlertForNotification(notificacion);
      }
    };

    socket.on('nueva_notificacion', handleNuevaNotificacion);
    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off('nueva_notificacion', handleNuevaNotificacion);
      }
    };
  }, [socket]);

  // ✅ HOTFIX3: Polling — cada 20s, verificar si hay notificaciones HOT/WARM no leídas
  // que no hayamos alertado (cubre desconexiones de socket)
  useEffect(() => {
    const checkUnreadAlerts = async () => {
      try {
        const result = await notificationService.getNotificaciones({ limit: 10, soloNoLeidas: true });
        const notifs = result.data || [];
        for (const notif of notifs) {
          const temp = notif.metadata?.temperatura;
          if (
            notif.tipo === 'LEAD_CALIFICADO' &&
            (temp === 'CALIENTE' || temp === 'TIBIO') &&
            !alertedIdsRef.current.has(notif.id)
          ) {
            // Solo alertar si fue creada hace menos de 5 minutos
            const age = Date.now() - new Date(notif.createdAt).getTime();
            if (age < 5 * 60 * 1000) {
              showAlertForNotification(notif);
            }
          }
        }
      } catch { /* silencioso */ }
    };

    // Check al montar
    checkUnreadAlerts();
    // Polling cada 20s
    const interval = setInterval(checkUnreadAlerts, 20000);
    return () => clearInterval(interval);
  }, []);

  // ============================================
  // Mostrar alerta para una notificación
  // ============================================
  const showAlertForNotification = useCallback((notificacion) => {
    if (alertedIdsRef.current.has(notificacion.id)) return; // Ya alertada
    alertedIdsRef.current.add(notificacion.id);

    const temp = notificacion.metadata?.temperatura;
    const id = ++alertIdRef.current;
    const alert = {
      id,
      notificacion,
      temperatura: temp,
      leadNombre: notificacion.metadata?.leadNombre || 'Nuevo lead',
      score: notificacion.metadata?.score || 0,
      createdAt: Date.now()
    };

    setAlerts(prev => [alert, ...prev].slice(0, 5));
    playAlertSound();

    // Auto-dismiss 45s
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }, 45000);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
  const handleGoToChat = useCallback((alert) => {
    setAlerts(prev => prev.filter(a => a.id !== alert.id));
    navigate('/chat');
  }, [navigate]);

  const handleDismiss = useCallback((alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // ============================================
  // RENDER
  // ============================================
  if (alerts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      padding: '12px 16px',
      pointerEvents: 'none'
    }}>
      {alerts.map((alert) => {
        const isHot = alert.temperatura === 'CALIENTE';
        const bgColor = isHot ? '#dc2626' : '#d97706';
        const bgGradient = isHot
          ? 'linear-gradient(135deg, #dc2626, #b91c1c, #991b1b)'
          : 'linear-gradient(135deg, #d97706, #b45309, #92400e)';
        const animName = isHot ? 'leadAlertPulse' : 'leadAlertPulseWarm';

        return (
          <div
            key={alert.id}
            style={{
              pointerEvents: 'auto',
              width: '100%',
              maxWidth: 700,
              background: bgGradient,
              borderRadius: 14,
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              animation: `leadAlertSlideIn 0.4s ease-out, ${animName} 2s infinite`,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            onClick={() => handleGoToChat(alert)}
          >
            {/* Progress bar auto-dismiss */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: 3,
              background: 'rgba(255,255,255,0.4)',
              borderRadius: '0 0 14px 14px',
              animation: 'progressShrink 45s linear forwards'
            }} />

            {/* Emoji pulsante */}
            <div style={{
              fontSize: 36,
              animation: 'urgentBlink 1s infinite',
              flexShrink: 0
            }}>
              {isHot ? '🔥' : '🟡'}
            </div>

            {/* Contenido */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: '#fff',
                fontSize: 16,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 2,
                textShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }}>
                {isHot ? '🚨 LEAD CALIENTE' : '⚡ LEAD TIBIO'} — ¡ATENDER URGENTE!
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.95)',
                fontSize: 14,
                fontWeight: 500
              }}>
                <strong>{alert.leadNombre}</strong> — Score: {alert.score} pts
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12,
                marginTop: 2
              }}>
                {alert.notificacion?.mensaje?.substring(0, 80) || 'Lead calificado por el bot. Contactar inmediatamente.'}
              </div>
            </div>

            {/* Botón IR AL CHAT */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleGoToChat(alert);
              }}
              style={{
                background: 'rgba(255,255,255,0.95)',
                color: bgColor,
                border: 'none',
                borderRadius: 10,
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: 800,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                transition: 'transform 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              💬 IR AL CHAT
            </button>

            {/* Botón cerrar */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss(alert.id);
              }}
              style={{
                position: 'absolute',
                top: 6,
                right: 8,
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                fontSize: 16,
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: 4
              }}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default LeadAlertOverlay;
